import React, { useContext, useEffect } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/shadcn/card";
import { UserContext } from "@/store/context/UserContext.tsx";
import { useTranslation } from "react-i18next";
import { constants } from "@/constants";
import { useQuery } from "react-query";
import { queryKey } from "@/store/queryKeys.ts";
import {Icon} from "@/components/Icon.tsx";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/shadcn/alert-dialog";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/shadcn/accordion";
import { Input } from "@/components/shadcn/input.tsx";
import { Button } from "@/components/shadcn/button.tsx";
import { CopyButton } from "@/components/shadcn-custom/copy-to-clipboard.tsx";
import Loading from "@/components/Loading";
import { EditRefCodeModal } from "@/components/billing/EditRefCodeModal.tsx";
import { ExternalLink } from "@/components/ExternalLink.tsx";

export const AffiliatePage: React.FC = () => {
  const { userSettings } = useContext(UserContext);
  const { t } = useTranslation();

  let partnerQuery = useQuery(queryKey.partner, async function () {
    let res = await fetch(`${constants.url.API_PREFIX}billing/partner`);
    return res.json();
  });

  const generateAffLink = (code: string): string => {
    return `https://most-ai.com?from=${partnerQuery.data.ref_code}`;
  };

  if (!partnerQuery.data) {
    return <Loading />;
  }

  return (
    <>
      <div className="grid grid-cols-1 gap-3">
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
          <Card>
            <CardHeader>
              <CardTitle>Зарабатывайте с нами</CardTitle>
              <CardDescription></CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-2 text-lg">
              <div>
                Получайте вознаграждение на ваш счёт с каждого платежа
                приглашенных вами пользователей. Заработанные деньги можно сразу
                использовать в сервисе или вывести на карту.
              </div>
            </CardContent>
            {/*<CardFooter className="flex justify-between"></CardFooter>*/}
          </Card>

          <Card>
            <CardContent className="text-right pt-4">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
                <div className="bg-gray-100 rounded-lg p-4 text-center font-semibold text-lg">
                  Приглашено
                  <br />
                  {partnerQuery.data && partnerQuery.data.invited}
                </div>
                <div className="bg-gray-100 rounded-lg p-4 text-center font-semibold text-lg">
                  Заработано
                  <br />
                  {partnerQuery.data && partnerQuery.data.earned.toFixed(2)}
                  &nbsp;₽
                </div>
                <div className="bg-gray-100 rounded-lg p-4 text-center font-semibold text-lg">
                  Вознаграждение
                  <br />
                  {partnerQuery.data && partnerQuery.data.percent}&nbsp;%
                </div>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button className="mt-4 text-right [&_svg]:size-5">
                    <Icon icon="famicons:card-outline" />
                    Вывод средств
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Вывод средств</AlertDialogTitle>
                  </AlertDialogHeader>
                  <div className="text-left text-md">
                    На текущий момент вывод средств осуществляется вручную нашим
                    менеджером через <b>Telegram</b>
                    <br />( временно ведутся работы по интеграции с банком)
                    <br />
                    <br />
                    <b>Способы перевода:</b>
                    <ul>
                      <li>- по номеру телефона (СБП)</li>
                      <li>- по номеру карты</li>
                      <li>- перевод на р/с юр.лица</li>
                      <li>- криптовалюта</li>
                    </ul>
                  </div>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Отмена</AlertDialogCancel>
                    <AlertDialogAction
                      onClick={() => {
                        window.open(
                          `https://t.me/Lego66?text=Вывод средств. Аккаунт ${userSettings.email}`,
                          "_blank",
                        );
                      }}
                    >
                      Продолжить
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>Поделитесь ссылкой</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="pb-4">
              Поделитесь вашей ссылкой с друзьями, коллегами, или разместите в
              социальных сетях.
              <br />
              <b>Специальные условия</b> для блогеров и авторов курсов по ИИ -
              напишите нам!
            </div>
            <div className="flex flex-col lg:flex-row gap-2">
              {partnerQuery.data && (
                <Input
                  value={generateAffLink(partnerQuery.data.ref_code)}
                  className="bg-gray-100"
                  onChange={() => {}}
                />
              )}

              <EditRefCodeModal refCode={partnerQuery.data.ref_code} />

              <CopyButton
                value={generateAffLink(partnerQuery.data.ref_code)}
                className="ml-1"
              />
            </div>
            <div className="mt-4">
              <span className="inline-block pb-2 font-semibold">
                Создать QR код
              </span>
              <br />
              <ExternalLink href="https://qr.io">
                Сервис QR.io
              </ExternalLink>{" "}
              позволяет создавать стилизованные QR-коды с вашим логотипом.
              <br />
              Дополнительные функции:
              <ul>
                <li>
                  - создание короткой ссылки на промокод формата "qr.fm/jldRyq"
                </li>
                <li>
                  - отслеживание количества сканирований и переходов по QR-коду
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Промокод</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="pb-4">
              Промокод помогает сильно повысить количество оплат.
              <br />
              <br />
              Применение промокода даёт <b>кэшбэк до 50% бонусами</b> при первой
              оплате, например: пользователь пополняет баланс на 1000руб и
              дополнительно получает
              <b> 500 бонусов</b> - не упускайте возможность рассказать об этом
              своей аудитории 😉
              <br />
              <br />
              Если вы не используете реферальные ссылки для привлечения
              пользователей или пользователь не перешёл по ней, то можно ввести
              промокод и пользователь будет считаться как привлечённый Вами.
            </div>
            <div className="flex flex-col lg:flex-row gap-2">
              {partnerQuery.data && (
                <Input
                  value={partnerQuery.data.ref_code}
                  className="bg-gray-100"
                  onChange={() => {}}
                />
              )}

              <CopyButton value={partnerQuery.data.ref_code} className="ml-1" />
            </div>
          </CardContent>
        </Card>

        <Card className="">
          <CardHeader className="relative">
            <CardTitle>FAQ</CardTitle>
            <CardDescription></CardDescription>
          </CardHeader>
          <CardContent>
            <Accordion
              type="single"
              collapsible
              className="w-full"
            >
              <AccordionItem value="item-3">
                <AccordionTrigger className="text-xl">
                  Как я могу заработать ?
                </AccordionTrigger>
                <AccordionContent className="text-lg">
                  Делитесь вашей ссылкой или промокодом в социальных сетях, с друзьями и коллегами и получайте вознаграждения за каждую оплату, совершенную приглашенными вами пользователями.
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-1">
                <AccordionTrigger className="text-xl">
                  Есть ли предел заработка ?
                </AccordionTrigger>
                <AccordionContent className="text-lg">
                  Ограничений по сумме нет! Наши топовые партнёры ежемесячно
                  зарабатывают десятки тысяч рублей
                </AccordionContent>
              </AccordionItem>
              <AccordionItem value="item-2">
                <AccordionTrigger className="text-xl">
                  Как вывести деньги ?
                </AccordionTrigger>
                <AccordionContent className="text-lg">
                  <p>
                    Вывод средств осуществляется нашим менеджером: нажмите
                    "вывод средств", укажите сумму вывода и способ вывода.
                  </p>
                  <br />
                  <b>Способы перевода:</b>
                  <ul>
                    <li>- по номеру телефона (СБП)</li>
                    <li>- по номеру карты</li>
                    <li>- перевод на р/с юр.лица</li>
                    <li>- криптовалюта</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>
      </div>
    </>
  );
};
